import { z } from 'zod';
import { OffsetDateTimeInputSchema, UtcDateTimeSchema } from './timestamps.js';
export const MAX_INVENTORY_QUANTITY = 9_000_000_000_000;
export const InventoryQuantitySchema = z.number().int().min(-MAX_INVENTORY_QUANTITY).max(MAX_INVENTORY_QUANTITY);
export const NonZeroInventoryDeltaSchema = InventoryQuantitySchema.refine((value) => value !== 0, 'Quantity change must not be zero');
export const InventoryOperationTypeSchema = z.enum([
    'MANUAL_ADJUSTMENT', 'STOCKTAKE', 'OPENING_BALANCE', 'SALE', 'REFUND',
    'PURCHASE_RECEIPT', 'INTERNAL_USE', 'MIGRATION', 'MIGRATION_OPENING_BALANCE', 'SYSTEM',
    'ADVICE_NOTE_ISSUE', 'ADVICE_NOTE_RETURN',
    'PARALLEL_MIGRATION_OPENING_STOCK',
]);
export const InventoryReasonCodeSchema = z.enum(['COUNT_CORRECTION', 'DAMAGED', 'LOST', 'FOUND', 'OPENING_BALANCE', 'OTHER']);
export const StockStatusSchema = z.enum(['NEGATIVE', 'OUT_OF_STOCK', 'LOW', 'OK']);
const QueryBoolean = z.preprocess((value) => value === 'true' ? true : value === 'false' ? false : value, z.boolean());
const PageFields = {
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
};
export const InventoryLevelQuerySchema = z.object({
    search: z.string().trim().max(160).optional(), productId: z.uuid().optional(), locationId: z.uuid().optional(), status: StockStatusSchema.optional(),
    categoryId: z.uuid().optional(), brandId: z.uuid().optional(), belowMinimum: QueryBoolean.optional(),
    activeOnly: QueryBoolean.default(true), ...PageFields,
}).strict();
export const InventoryMovementQuerySchema = z.object({
    operationId: z.uuid().optional(), productId: z.uuid().optional(), locationId: z.uuid().optional(), operationType: InventoryOperationTypeSchema.optional(),
    actorUserId: z.uuid().optional(), reference: z.string().trim().max(160).optional(),
    from: OffsetDateTimeInputSchema.optional(), to: OffsetDateTimeInputSchema.optional(), ...PageFields,
}).strict().refine((value) => !value.from || !value.to || value.from <= value.to, { message: 'From must not be after to' });
export const ManualAdjustmentRequestSchema = z.object({
    productId: z.uuid(), locationId: z.uuid(), deltaQuantity: NonZeroInventoryDeltaSchema,
    reasonCode: InventoryReasonCodeSchema, note: z.string().trim().max(2_000).nullable().optional().default(null),
}).strict().superRefine((value, context) => {
    if (value.reasonCode === 'OTHER' && !value.note)
        context.addIssue({ code: 'custom', path: ['note'], message: 'A note is required for OTHER' });
});
export const StocktakeRequestSchema = z.object({
    productId: z.uuid(), locationId: z.uuid(), countedQuantity: InventoryQuantitySchema,
    reasonCode: z.enum(['COUNT_CORRECTION', 'OTHER']).default('COUNT_CORRECTION'),
    note: z.string().trim().max(2_000).nullable().optional().default(null),
}).strict().superRefine((value, context) => {
    if (value.reasonCode === 'OTHER' && !value.note)
        context.addIssue({ code: 'custom', path: ['note'], message: 'A note is required for OTHER' });
});
export const InventoryMovementResultSchema = z.object({
    id: z.uuid(), productId: z.uuid(), locationId: z.uuid(), deltaQuantity: InventoryQuantitySchema,
    quantityBefore: InventoryQuantitySchema, quantityAfter: InventoryQuantitySchema, occurredAt: UtcDateTimeSchema,
});
export const InventoryOperationResultSchema = z.object({
    changed: z.boolean(), operationId: z.uuid().nullable(), operationType: InventoryOperationTypeSchema,
    movements: z.array(InventoryMovementResultSchema),
});
export const InventoryLevelSchema = z.object({
    productId: z.uuid(), productName: z.string(), productCode: z.string().nullable(), primaryBarcode: z.string().nullable(),
    productStatus: z.enum(['ACTIVE', 'ARCHIVED']), categoryId: z.uuid().nullable(), categoryName: z.string().nullable(),
    brandId: z.uuid().nullable(), brandName: z.string().nullable(), locationId: z.uuid(), locationCode: z.string(),
    locationName: z.string(), quantity: InventoryQuantitySchema, minimumLevel: z.number().int().nonnegative().nullable(),
    maximumLevel: z.number().int().nonnegative().nullable(), status: StockStatusSchema,
});
export const InventoryLevelListResponseSchema = z.object({ items: z.array(InventoryLevelSchema), page: z.number(), pageSize: z.number(), total: z.number() });
export const InventoryMovementHistorySchema = InventoryMovementResultSchema.extend({
    operationId: z.uuid(), operationType: InventoryOperationTypeSchema, reasonCode: z.string().nullable(), note: z.string().nullable(),
    productName: z.string(), productCode: z.string().nullable(), locationCode: z.string(), locationName: z.string(),
    actorType: z.enum(['USER', 'DEVICE', 'SYSTEM']), actorId: z.uuid().nullable(), actorName: z.string().nullable(),
    referenceType: z.string().nullable(), referenceId: z.string().nullable(),
});
export const InventoryMovementListResponseSchema = z.object({ items: z.array(InventoryMovementHistorySchema), page: z.number(), pageSize: z.number(), total: z.number() });
export const InventoryOperationDetailSchema = z.object({
    id: z.uuid(), operationType: InventoryOperationTypeSchema, actorType: z.enum(['USER', 'DEVICE', 'SYSTEM']),
    actorUserId: z.uuid().nullable(), actorDeviceId: z.uuid().nullable(), referenceType: z.string().nullable(), referenceId: z.string().nullable(),
    reasonCode: z.string().nullable(), note: z.string().nullable(), metadata: z.record(z.string(), z.unknown()),
    occurredAt: UtcDateTimeSchema, createdAt: UtcDateTimeSchema,
});
export const InventoryOperationDetailResponseSchema = z.object({ operation: InventoryOperationDetailSchema, movements: z.array(InventoryMovementHistorySchema) });
export const InventoryReconciliationQuerySchema = z.object(PageFields).strict();
export const InventoryDiscrepancySchema = z.object({
    productId: z.uuid(), productName: z.string(), locationId: z.uuid(), locationName: z.string(),
    balanceQuantity: InventoryQuantitySchema, ledgerQuantity: InventoryQuantitySchema, difference: InventoryQuantitySchema,
});
export const InventoryReconciliationResponseSchema = z.object({
    healthy: z.boolean(), items: z.array(InventoryDiscrepancySchema), page: z.number(), pageSize: z.number(), total: z.number(), checkedAt: UtcDateTimeSchema,
});
